import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus } from "lucide-react";
import { Img } from "../common/Img";
import { Reveal } from "../common/Reveal";
import { CATEGORIES } from "../../data/categories";
import { useLanguage } from "../../context/LanguageContext";
import { fadeUp, viewport, stagger } from "../../lib/animations";

// Task 05 (C05) — direct access: each category opens the catalog already
// filtered by that subcategory. "Other Flowers" browses the whole catalog.
export default function ExploreFlowers() {
  const { t, localize } = useLanguage();
  const navigate = useNavigate();

  const openCategory = (category) => {
    if (category.id === "other-flowers") {
      navigate("/catalog");
    } else {
      navigate(`/catalog?categories=${category.id}`);
    }
  };

  return (
    <section
      id="products"
      className="bg-[#F6F2EC] py-24 md:py-32"
      data-testid="explore-section"
    >
      <div className="container-tessa">
        <div className="mb-14 flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <Reveal>
            <span className="font-sans text-xs uppercase tracking-[0.24em] text-[#D92A47]">
              {t("explore.eyebrow")}
            </span>
            <h2 className="mt-4 font-serif text-5xl leading-[0.95] text-[#183C32] md:text-6xl lg:text-7xl">
              {t("explore.title1")}
              <br />
              <span className="italic text-[#D92A47]">{t("explore.title2")}</span>
            </h2>
          </Reveal>
        </div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
          variants={stagger(0.08)}
          className="grid auto-rows-[240px] grid-cols-1 gap-3 sm:grid-cols-2 md:auto-rows-[280px] md:gap-4 lg:grid-cols-4"
        >
          {CATEGORIES.map((category) => (
            <motion.button
              key={category.id}
              variants={fadeUp}
              onClick={() => openCategory(category)}
              data-testid={`category-${category.id}`}
              className={`group relative overflow-hidden text-left ${category.span}`}
            >
              <Img
                src={category.image}
                alt={category.name}
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent transition-opacity duration-500 group-hover:from-black/90" />
              <div className="absolute inset-0 flex flex-col justify-end p-6">
                <div className="flex items-end justify-between">
                  <div>
                    <h3 className="font-serif text-2xl leading-tight text-[#F6F2EC] md:text-3xl">
                      {category.name}
                    </h3>
                    <span className="text-[13px] text-[#F6F2EC]/70">
                      {localize(category, "count")}
                    </span>
                  </div>
                  <span className="grid h-10 w-10 translate-y-2 place-items-center rounded-full border border-[#F6F2EC]/40 text-[#F6F2EC] opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                    <Plus size={18} />
                  </span>
                </div>
                <span className="mt-3 inline-block text-[11px] uppercase tracking-[0.18em] text-[#C4A46B] opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                  {t("explore.discover")}
                </span>
              </div>
            </motion.button>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
